import React, { useCallback, useEffect, useState } from 'react'
import {
  BaseRecord,
  getDefaultFilter,
  IResourceComponentsProps,
} from '@refinedev/core'
import { useTable, List, useModalForm } from '@refinedev/antd'
import { Button, Table } from 'antd'
import '../styles/index.css'
import { Row } from '../components/interface'
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook'
import { ButtonListHeader } from '../components/buttonsList'
import { ColumnType } from 'antd/es/table'
import { NewBankModalCreate } from '../../components/modal/admin/addBank'
import { useModalForm as useModalFormAntd } from 'sunflower-antd'
import { EditBank } from '../../components/modal/admin/editBank'
import { TableForm } from '../crypto-withdrawal/tableForm'
import { onSearch } from '../../utils/list.utils'
import { DeleteOutlined } from '@ant-design/icons'
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook'
import { confirmModalDeleteProps } from '../components/constants'
import { useDeleteBank } from '../../utils/hooks/useDeleteBank.hook'
import { useUserDetails } from '../../components/pages/auth/components/auth/hooks'

export const BanksList: React.FC<IResourceComponentsProps> = () => {
  const details = useUserDetails()
  const [editingId, setEditingId] = useState<number>()
  const [deletingId, setDeletingId] = useState<number>()
  const {
    tableProps,
    searchFormProps,
    filters,
    setCurrent,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    onSearch,
  })

  const isAdminRole = useIsAdminRole()

  const {
    modalProps: createBankModalProps,
    formProps: createBankFormProps,
    show: createBankModalShow,
    formLoading: createBankFormLoading,
    submit: createBankFormSubmit,
    close: createBankModalClose,
  } = useModalForm({
    warnWhenUnsavedChanges: false,
    action: 'create',
  })

  const {
    close: editModalClose,
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
  } = useModalFormAntd({})

  const handleClickOnCell = (record: BaseRecord) => {
    return {
      onClick: () => {
        if (record.id && isAdminRole) {
          setEditingId(Number(record.id))
        }
      },
    }
  }

  const { deleteBank } = useDeleteBank()

  const reloadListAfterDelete = useCallback(async () => {
    const result = await refetchList()
    const pagination = tableProps.pagination
    if (!pagination || typeof pagination === 'boolean') return

    const responseData: any = result?.data
    const pageSize = Number(pagination.pageSize || 10)
    const currentPage = Number(pagination.current || 1)
    const responseTotal = Number(
      responseData?.total ??
      responseData?.totalCount ??
      responseData?.data?.total ??
      responseData?.data?.totalCount
    )

    const total = Number.isNaN(responseTotal)
      ? Number(pagination.total || 0)
      : responseTotal
    const targetPage = Math.max(1, Math.ceil(total / pageSize))

    if (currentPage > targetPage) {
      if (setCurrent) {
        setCurrent(targetPage)
      } else {
        pagination.onChange?.(targetPage, pageSize)
      }
    }
  }, [refetchList, setCurrent, tableProps.pagination])

  const onOkDelete = useCallback(async () => {
    if (deletingId !== undefined) {
      setDeletingId(undefined)
      await deleteBank(deletingId)
      await reloadListAfterDelete()
    }
  }, [deleteBank, deletingId, reloadListAfterDelete])

  const onCancelDelete = useCallback(
    () => setDeletingId(undefined),
    [setDeletingId]
  )

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  })

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification()
    }
  }, [deletingId, openNotification])

  const columns: ColumnType<Row>[] = [
    {
      dataIndex: 'id',
      title: 'ID',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: 'name',
      title: 'Name',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: 'tin',
      title: 'TIN/RegNo',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'intermediaryBank',
      title: 'Intermediary Bank',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'swift',
      title: 'SWIFT',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'legalAddress',
      title: 'Legal Address',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'phoneNumber1',
      title: 'Tel 1',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'phoneNumber2',
      title: 'Tel 2',
      onCell: handleClickOnCell,
    },
    {
      render: (value, data) => {
        return (
          isAdminRole && (
            <Button
              className={'list-button list-button--delete'}
              icon={<DeleteOutlined style={{ fontSize: '19px' }} />}
              size='small'
              onClick={() => setDeletingId(data.id)}
            />
          )
        )
      },
    },
  ]

  return (
    <>
      <List
        title='Banks Directory'
        headerButtons={null}
      >
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter('', filters),
          }}
          onSubmit={searchFormProps.form?.submit}
          searchPlaceholder={'Search banks by SWIFT or name'}
          showDateFilter={false}
          headerButtons={
            isAdminRole ? ButtonListHeader(createBankModalShow) : null
          }
        />
        <Table
          {...tableProps}
          rowKey='id'
          columns={columns as any}
          dataSource={tableProps.dataSource}
        />
      </List>
      {contextHolder}
      <NewBankModalCreate
        closeModal={createBankModalClose}
        formProps={createBankFormProps}
        modalProps={createBankModalProps}
        formIsLoading={createBankFormLoading}
        formSubmit={createBankFormSubmit}
        refetchList={refetchList}
        customerId={details?.id}
      />
      <EditBank
        editingId={editingId}
        changeEditingId={setEditingId}
        show={editModalShow}
        formProps={editFormProps}
        modalProps={editModalProps}
        refetchList={refetchList}
        closeModal={editModalClose}
      />
    </>
  )
}
